import "./WorkflowTaskPanel.css";

import { ReactElement } from "react";

import { SelectData } from "../../models";
import { GetTaskBoardResponse, SearchWorkflowTasksResponse } from "../../service/query";
import { hasWorkflowOrganisationAccessLevel } from "../../utils";
import { EmptyFlyoutPanel } from "../../widget";
import { CompleteTaskModal, TaskPanelLeft, TaskPanelRight, TaskPanelTop } from "./components";
import { DeleteTaskModal } from "./components/DeleteTaskModal";
import { useWorkflowTaskPanel } from "./useWorkflowTaskPanel";

interface WorkflowTaskPanelProps {
  /*
    If undefined, then create
    If defined, then update
  */
  task?: SearchWorkflowTasksResponse["results"][0];
  currentStepUuid: string | undefined;
  workflow?: GetTaskBoardResponse["results"][number];
  show: boolean;
  phaseOptions?: SelectData;
  phaseLabel?: string;
  isRepeatablePhase?: (phase: string | null | undefined) => boolean;

  refresh: boolean;
  handleRefresh: () => void;

  onClose: () => void;
}

export const WorkflowTaskPanel = ({
  task,
  currentStepUuid,
  workflow,
  show,
  phaseOptions,
  phaseLabel,
  isRepeatablePhase,

  refresh,
  handleRefresh,

  onClose,
}: WorkflowTaskPanelProps): JSX.Element => {
  const {
    isCreateTask,
    isDealWorkflow,
    currentUserType,

    stepSelectData,
    activitySelectData,
    // NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
    // phaseKeyDatesSelectData,
    organisationsSelectData,
    visibilityOrganisationsSelectData,

    workflowOrganisationAccessLevel,
    hasFullEditAccess,

    errors,
    setErrors,

    currentTask,
    setCurrentTask,

    isTitleEditable,
    setIsTitleEditable,

    showDeleteTaskModal,
    setShowDeleteTaskModal,

    showCompletePhaseModal,
    setShowCompletePhaseModal,

    handleLinkedActivityOnClick,
    handleCloseTaskPanel,
    handleSubmit,
    updateStepsSelectDataByPhase,
    isHandleSubmitLoading,
    defaultSelectedOrgTypeVisibility,
  } = useWorkflowTaskPanel({
    task,
    currentStepUuid,
    workflow,
    show,
    refresh,
    handleRefresh,

    onClose,
  });

  const content = (): ReactElement => {
    return (
      <div className="WorkflowTaskPanelContent">
        <TaskPanelTop
          handleCloseTaskPanel={handleCloseTaskPanel}
          handleClickDeleteTask={() => setShowDeleteTaskModal(true)}
          task={task}
          hasFullEditAccess={hasFullEditAccess}
        />
        <TaskPanelLeft
          errors={errors}
          hasFullEditAccess={hasFullEditAccess}
          isCreateTask={isCreateTask}
          isTitleEditable={isTitleEditable}
          setIsTitleEditable={setIsTitleEditable}
          currentTask={currentTask}
          setCurrentTask={setCurrentTask}
          handleSubmit={handleSubmit}
          handleCloseTaskPanel={handleCloseTaskPanel}
          isHandleSubmitLoading={isHandleSubmitLoading}
          handleLinkedActivityOnClick={handleLinkedActivityOnClick}
          workflow={workflow}
          currentUserType={currentUserType}
          activitySelectData={activitySelectData}
          workflowOrganisationAccessLevel={workflowOrganisationAccessLevel}
        />
        {workflowOrganisationAccessLevel && (
          <TaskPanelRight
            organisationsSelectData={organisationsSelectData}
            visibilityOrganisationsSelectData={visibilityOrganisationsSelectData}
            currentTask={currentTask}
            setCurrentTask={setCurrentTask}
            isDealWorkflow={isDealWorkflow}
            stepSelectData={stepSelectData}
            // NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
            // phaseKeyDatesSelectData={phaseKeyDatesSelectData}
            hasWorkflowOrganisationAccessLevel={hasWorkflowOrganisationAccessLevel}
            workflowOrganisationAccessLevel={workflowOrganisationAccessLevel}
            hasFullEditAccess={hasFullEditAccess}
            defaultSelectedOrgTypeVisibility={defaultSelectedOrgTypeVisibility}
            activitySelectData={activitySelectData}
            updateStepsSelectDataByPhase={updateStepsSelectDataByPhase}
            phaseOptions={phaseOptions}
            phaseLabel={phaseLabel}
            isRepeatablePhase={isRepeatablePhase}
          />
        )}
        <DeleteTaskModal
          show={showDeleteTaskModal}
          closeModal={() => setShowDeleteTaskModal(false)}
          closePanel={handleCloseTaskPanel}
          handleRefresh={handleRefresh}
          task={task}
        />
        <CompleteTaskModal
          show={showCompletePhaseModal}
          task={currentTask}
          closeModal={() => setShowCompletePhaseModal(false)}
          closePanel={handleCloseTaskPanel}
          handleRefresh={handleRefresh}
          setErrors={setErrors}
        />
      </div>
    );
  };

  return <EmptyFlyoutPanel show={show} onClose={handleCloseTaskPanel} size="Large" contentChildren={content()} />;
};
